import React, {Dispatch, SetStateAction, useContext, useEffect, useState} from "react";
import { Linking, TextInput, TouchableOpacity, View } from "react-native";
import PtDocPicker from "../pt-doc-picker/pt-doc-picker";
import styles from "./pt-file-uploader-styles";
import PtText from "../core/pt-text/pt-text";
import PtCallout from "../core/pt-callout/pt-callout";
import { uploadFile, xlsTemplateUrl } from "../../actions/fileUploadActions";
import PtActivityIndicator from "../core/pt-activity-indicator/pt-activity-indicator";
import { useDispatch, useSelector } from "react-redux";
import { getTranslation } from "../../helpers/getTranslation";
import { styleVars } from "../../app-style-vars";
import {
  PtFileUploaderFilePickerStatus,
  PtFileUploaderLabels,
  PtFileUploaderOrderScenario,
  PtFileUploaderPaymentMode,
  PtFileUploaderProps,
  PtFileUploaderSettings,
  PtFileUploaderUploadStatus
} from './pt-file-uploader-props';
import { Picker } from '@react-native-community/picker';
import PtIcon from '../core/pt-icon/pt-icon';
import { getCustomerDataFromApi } from '../../actions/customerDataActions';
import { getOrderTypesFromApi } from '../../actions/orderTypeActions';
import UserContext from "../../helpers/UserContext";

/** pulled out of function body to reduce cognitive complexity */
const getLabels = (props: PtFileUploaderProps) => {
  return {
    pickerButtonLabel: props.pickerButtonLabel ? props.pickerButtonLabel : getTranslation('file.upload.button.text') || 'Pick a file to upload',
    pickerButtonIcon: props.pickerButtonIcon ? props.pickerButtonIcon : 'document',
    cancelButtonLabel: props.cancelLabel ? props.cancelLabel : getTranslation('file.upload.cancel.label') || 'Cancel',
    fileUploaderTitle: props.title || getTranslation('file.upload.title') || 'Upload a file',
    fileUploaderSubtitle: props.subtitle || '',
    fileUploaderExplanation: props.explanationText || '',
    fileUploaderNote: getTranslation('file.upload.note.orders.xls') || '',
    uploadAnotherLabel: props.uploadAnotherLabel || getTranslation('file.upload.upload.another.label') || 'Upload another?',
    resetLabel: props.resetLabel || getTranslation('file.upload.reset.label') || 'Reset',
    noAuthText: props.noAuthText || getTranslation('file.upload.no.auth.text') || 'File upload disabled until you log in',
    fileUploadDownloadTemplate: getTranslation('file.upload.download.template.xls'),
    fileUploadAfterUploading: getTranslation('file.upload.after.uploading.xls'),
    fileUploadPaymentTerm: getTranslation('file.upload.payment.term.xls'),
    fileUploadPaymentTermPlaceholder: getTranslation('file.upload.payment.term.placeholder.xls'),
    fileUploadOrderType: getTranslation('file.upload.order.type.xls'),
    fileUploadOrderButtonLabel: getTranslation('file.upload.order.button.label'),
    fileUploadOrderTypePlaceholder: getTranslation('file.upload.order.type.placeholder.xls'),
    fileUploadNotes: getTranslation('file.upload.notes.xls'),
    fileUploadNotesPlaceholder: getTranslation('file.upload.notes.placeholder.xls'),
    fileUploadPurchaseOrder: getTranslation('file.upload.purchase.order.xls'),
    fileUploadPurchaseOrderPlaceholder: getTranslation('file.upload.purchase.order.placeholder.xls'),
  }
};

const getSettings = (props: PtFileUploaderProps) => {
  return {
    fileType: props.fileType || '*/*',
    fileUploadType: props.uploadType ? props.uploadType : 'orders',
    successMessage: props.successMessage ? props.successMessage : 'Upload successful',
  }
};

const getInitialStateRender = (settings: PtFileUploaderSettings, labels: PtFileUploaderLabels, uploadStatus: PtFileUploaderUploadStatus, setFilePickerStatus: Dispatch<SetStateAction<PtFileUploaderFilePickerStatus>>, setUploadStatus: Dispatch<SetStateAction<PtFileUploaderUploadStatus>>) => {
  return (
    <>
      {!uploadStatus.uploadError && !uploadStatus.uploadSuccess ?
        <PtDocPicker
          pickerStatus={(newStatus: PtFileUploaderFilePickerStatus) => setFilePickerStatus(newStatus)}
          uploadStatus={(newStatus: PtFileUploaderUploadStatus) => setUploadStatus(newStatus)}
          fileType={settings.fileType}
          buttonIcon={labels.pickerButtonIcon}
          buttonLabel={labels.pickerButtonLabel}
        />
        : null
      }
    </>
  );
};

const getUploadDoneRender = (settings: PtFileUploaderSettings, labels: PtFileUploaderLabels, uploadStatus: PtFileUploaderUploadStatus, uploadAnother: () => void, getErrorMessages: any, clearUploadError: () => void) => {
  return (
    uploadStatus.uploadSuccess ?
      <View>
        <PtCallout type={'success'} message={uploadStatus.uploadSuccess && uploadStatus.payload ? uploadStatus.payload : settings.successMessage} />
        <TouchableOpacity onPress={() => uploadAnother()}>
          <PtText style={{ padding: styleVars.rem(.5), textDecorationLine: 'underline' }}>{labels.uploadAnotherLabel}</PtText>
        </TouchableOpacity>
      </View>
      :
      <View>
        <PtCallout type={'error'} message={getErrorMessages(uploadStatus.payload)} />
        <TouchableOpacity onPress={() => clearUploadError()}>
          <PtText style={{ padding: styleVars.rem(.5) }}>{labels.resetLabel}</PtText>
        </TouchableOpacity>
      </View>
  );
};

export default function PtFileUploader(props: PtFileUploaderProps) {

  const settings: PtFileUploaderSettings = getSettings(props);
  const labels: PtFileUploaderLabels = getLabels(props);

  const currentLanguage: string = useSelector((state: any) => state.translationsReducer.currentLanguage);
  const isAuthenticated: boolean = useSelector((state: any) => state.authReducer.isAuthenticated);
  const customerData: any = useSelector((state: any) => state.customerDataReducer);
  const orderTypesFromRedux: any[] = useSelector((state: any) => state.orderTypesReducer.orderTypes);

  const {userData, updateUserData} = useContext(UserContext);

  const [filePickerStatus, setFilePickerStatus] = useState<PtFileUploaderFilePickerStatus>({
    filePicked: false,
    fileName: '',
    fileSize: '',
    fileUri: ''
  });

  const [uploadStatus, setUploadStatus] = useState<PtFileUploaderUploadStatus>({
    uploadError: false,
    uploadSuccess: false,
    payload: undefined
  });

  const [isVisible, setIsVisible] = useState<boolean>(false);

  const dispatch = useDispatch();
  
  useEffect(() => {
    if (isAuthenticated && !customerData.dataCollected) {
      getCustomerDataFromApi(dispatch)
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && userData.salesAreaDependantCustomerUid && props.uploadType === 'orders/xls') {
      getOrderTypesFromApi(dispatch, userData.salesAreaDependantCustomerUid);
    }
  }, [isAuthenticated, userData.salesAreaDependantCustomerUid, props.uploadType]);

  useEffect(() => {
    if(props.uploadType === 'customers' ||
      (props.uploadType === 'orders' && !customerData.isDiSalesRep && !customerData.isDiDealer) ||
      (props.uploadType === 'orders/xls' && (customerData.isHeadquarters ||
          customerData.isDealer ||
          customerData.isSalesRep ||
          customerData.isPortalMaster ||
          customerData.isDistributor
        )
    )) {
      setIsVisible(true);
    }
    if(props.uploadType === 'orders/xls') {
      const availableOrderScenarios = orderTypesFromRedux.length > 0 ? orderTypesFromRedux : userData?.orderScenarios;
      availableOrderScenarios?.map((orderScenario: PtFileUploaderOrderScenario, id: number) => {
        if(orderScenario.default) {
          setOrderScenario(orderScenario.process);
          onOrderScenarioChange(orderScenario.process);
        }
      });
    }
  }, [customerData, userData, orderTypesFromRedux]);

  useEffect(() => {
    /** reset xls upload upon impersonation change*/
    if(props.uploadType === 'orders/xls' && userData.resetXlsUpload) {
      console.log('+++ reseting XLS upload after impersonation change');
      resetFileAndStatus();
      updateUserData({...userData, resetXlsUpload: false});
    }
    if(props.uploadType === 'orders' && userData.resetTxtUpload) {
      console.log('+++ reseting TXT upload after impersonation change');
      resetFileAndStatus();
      updateUserData({...userData, resetTxtUpload: false});
    }
    if(props.uploadType === 'customers' && userData.resetCustomerUpload) {
      console.log('+++ reseting CUS upload after impersonation change');
      resetFileAndStatus();
      updateUserData({...userData, resetCustomerUpload: false});
    }
  },[userData])

  const [uploading, setUploading] = useState(false);
  const [paymentModes, setPaymentModes] = useState<PtFileUploaderPaymentMode[]>([]);
  const [paymentTerm, setPaymentTerm] = useState<number | string>('');
  const [paymentTermError, setPaymentTermError] = useState<boolean>(false);
  const [orderScenario, setOrderScenario] = useState<number | string>('');
  const [orderScenarioError, setOrderScenarioError] = useState<boolean>(false);
  const [notes, setNotes] = useState('');
  const [purchaseOrder, setPurchaseOrder] = useState('');
  const [purchaseOrderError, setPurchaseOrderError] = useState<boolean>(false);

  useEffect(() =>{
    setPurchaseOrderError(!!purchaseOrder);
    console.log(!!purchaseOrder);
  },[purchaseOrder]);

  const validateForm = () => {
    if(settings.fileUploadType !== 'orders/xls' || ((paymentModes.length === 0 || paymentTerm) && orderScenario)) {
      startFileUpload()
    } else {
      if(paymentModes.length > 0 && !paymentTerm) {
        setPaymentTermError(true);
      }
      if(!orderScenario) {
        setOrderScenarioError(true);
      }
    }
  };
  
  const startFileUpload = async () => {
    if (isAuthenticated) {
      setUploading(true);

      const customerUid = userData.impersonatingUid || userData.salesAreaDependantCustomerUid;
      uploadFile(filePickerStatus.fileUri, filePickerStatus.fileName, settings.fileUploadType, currentLanguage, orderScenario as string, paymentTerm as string, notes, purchaseOrder, customerUid)
        .then((uploadRes: any) => {
          console.log('got back from action = ', uploadRes);
          if (uploadRes.status === 'success') {
            if(settings.fileUploadType === 'orders/xls') {
              settings.successMessage = uploadRes.detail;
            }
            setUploadStatus({
              uploadSuccess: true,
              uploadError: false,
              payload: settings.successMessage,
            });
          } else {
            setUploadStatus({
              uploadSuccess: false,
              uploadError: true,
              payload: uploadRes,
            })
          }
        }).finally(() => {
          setUploading(false)
        })
    } else {
      alert('You need to log in first'); // should not happen, user should be prevented from accessing this part if not authenticated
    }
  };

  const resetFileAndStatus = () => {
    setFilePickerStatus({
      filePicked: false,
      fileName: '',
      fileSize: '',
      fileUri: ''
    });
    setUploadStatus({
      uploadSuccess: false,
      uploadError: false,
      payload: undefined
    });
    setNotes('');
    setPurchaseOrder('');
    setPaymentTerm('');
  };

  const cancelUpload = () => {
    resetFileAndStatus();
  };

  const uploadAnother = () => {
    resetFileAndStatus();
  };

  const clearUploadError = () => {
    resetFileAndStatus();
  };

  const getErrorMessages = (errorResponseObject: any) => {
    console.log('in getErrorMessages, param = ', errorResponseObject);
    if (Array.isArray(errorResponseObject)) {
      const messages = errorResponseObject.map((singleMessage: any) => {
        if (typeof singleMessage === 'string'){
          singleMessage = JSON.parse(singleMessage)
        }
        return singleMessage['detail']
      });
      console.log(messages)
      return messages.join('\n');
    } else {
      return errorResponseObject.detail;
    }
  };

  const renderInitialState = (settings: PtFileUploaderSettings, labels: PtFileUploaderLabels, uploadStatus: PtFileUploaderUploadStatus, setFilePickerStatus: Dispatch<SetStateAction<PtFileUploaderFilePickerStatus>>, setUploadStatus: Dispatch<SetStateAction<PtFileUploaderUploadStatus>>) => {
    return getInitialStateRender(settings, labels, uploadStatus, setFilePickerStatus, setUploadStatus);
  };

  const renderUploadDone = (settings: PtFileUploaderSettings, labels: PtFileUploaderLabels, uploadStatus: PtFileUploaderUploadStatus, uploadAnother: () => void, getErrorMessages: any, clearUploadError: () => void) => {
    return getUploadDoneRender(settings, labels, uploadStatus, uploadAnother, getErrorMessages, clearUploadError);
  };

  const disabledButtonStyle = !isAuthenticated ? styles.uploadButtonDisabled : null;
  const disabledTextStyle = !isAuthenticated ? styles.uploadButtonTextDisabled : null;

  const onPaymentStateChange = (newValue: number | string) => {
    setPaymentTermError(!newValue);
    setPaymentTerm(newValue);
  };

  const onOrderScenarioChange = (newValue: number | string) => {
    setOrderScenarioError(!newValue);
    setOrderScenario(newValue);
    setPaymentTerm('');
    const availableOrderScenarios = orderTypesFromRedux.length > 0 ? orderTypesFromRedux : userData.orderScenarios;
    var chosenScenario = availableOrderScenarios.find((orderScenario: PtFileUploaderOrderScenario) => {
      return orderScenario.process === newValue;
    });
    setPaymentModes(chosenScenario ? chosenScenario.paymentModes : []);
  };
  
  const renderOrderTypes = (orderScenarios: PtFileUploaderOrderScenario[]) => {
      if(orderScenarios.length === 1) {
        let orderScenarioLabel: string = orderScenarios[0].names[currentLanguage];
        setTimeout(() => {
          setOrderScenario(orderScenarios[0].process);
        }, 0);
        return (
          <PtText>
            {orderScenarioLabel}
          </PtText>
        );
      } else {
        return (
          <Picker
            selectedValue={orderScenario}
            style={orderScenarioError ? [styles.picker, styles.pickerWithError] : styles.picker}
            onValueChange={(itemValue) => {
              onOrderScenarioChange(itemValue);
            }}
            enabled={!(uploadStatus.uploadError || uploadStatus.uploadSuccess)}
          >
            <Picker.Item value={''} label={labels.fileUploadOrderTypePlaceholder} />
            {orderScenarios.map((singleOrderScenario: PtFileUploaderOrderScenario, id: number) => {
              let paymentModeLabel: string = singleOrderScenario.names[currentLanguage];
              return <Picker.Item value={singleOrderScenario.process} label={paymentModeLabel} key={`orderScenario-${singleOrderScenario.process}`}/>;
            })}
          </Picker>
        );
      }
  };
  
  const renderPaymentModes = (paymentModes: PtFileUploaderPaymentMode[]) => {
    if(paymentModes.length === 1) {
      let paymentModeLabel: string = paymentModes[0].names[currentLanguage];
      setTimeout(() => {
        onPaymentStateChange(paymentModes[0].code);
      }, 0);
      return (
        <PtText>
          {paymentModeLabel}
        </PtText>
      );
    } else {
      return (
        <Picker
          selectedValue={paymentTerm}
          style={paymentTermError ? [styles.picker, styles.pickerWithError] : styles.picker}
          onValueChange={(itemValue) => {
            onPaymentStateChange(itemValue);
          }}
          enabled={!(uploadStatus.uploadError || uploadStatus.uploadSuccess)}
        >
          <Picker.Item value={''} label={labels.fileUploadPaymentTermPlaceholder} />
          {paymentModes.map((paymentMode: PtFileUploaderPaymentMode, id: number) => {
            let paymentModeLabel: string = paymentMode.names[currentLanguage];
            return <Picker.Item value={paymentMode.code} label={paymentModeLabel} key={`paymadeMode-${paymentMode.code}`}/>;
          })}
        </Picker>
      );
    }
  };
  
  return (
    <>
      {isVisible ? (
        isAuthenticated ?
        <View style={(props.uploadType === 'customers' || props.uploadType === 'orders/xls') && styles.withSeparator}>
          <PtActivityIndicator show={uploading} />
          <View>
            <PtText style={styles.uploaderTitle}>{`${labels.fileUploaderTitle} ${labels.fileUploaderSubtitle}`}</PtText>
          </View>

          <View>
            <PtText style={{ fontSize: styleVars.rem(.875) }}>
              {labels.fileUploaderExplanation}
            </PtText>
          </View>
          {settings.fileUploadType === 'orders/xls' && <View>
            <PtText style={{ fontSize: styleVars.rem(.875) }}>
              {labels.fileUploaderNote}
            </PtText>
          </View>}
          {settings.fileUploadType === 'orders/xls' && <TouchableOpacity onPress={() => Linking.openURL(xlsTemplateUrl(currentLanguage))} accessibilityRole={'link'}>
            <PtText style={{color:styleVars.boschDarkBlue, paddingTop:styleVars.rem(.5)}}>
              <PtIcon iconName={'download-frame'} iconSize={16} style={{paddingRight:styleVars.rem(.5), color:styleVars.boschDarkBlue}}/>
              {labels.fileUploadDownloadTemplate}
            </PtText>
          </TouchableOpacity>}

          {renderInitialState(settings, labels, uploadStatus, setFilePickerStatus, setUploadStatus)}
          {settings.fileUploadType === 'orders/xls' && <>
            <PtText style={{ fontSize: styleVars.rem(.875) }}>{labels.fileUploadAfterUploading}</PtText>
            <View style={styles.subtitleWrapper}>
              <PtText style={styles.subtitle}>{labels.fileUploadOrderType}<PtText style={styles.asterix}>*</PtText></PtText>
            </View>

            {(() => {
              const availableOrderScenarios = orderTypesFromRedux.length > 0 ? orderTypesFromRedux : userData?.orderScenarios;
              return availableOrderScenarios ? renderOrderTypes(availableOrderScenarios) : null;
            })()}
            {paymentModes && paymentModes.length > 0 ?
              <>
                <View style={styles.subtitleWrapper}>
                  <PtText style={styles.subtitle}>{labels.fileUploadPaymentTerm}<PtText style={styles.asterix}>*</PtText></PtText>
                </View>
                {renderPaymentModes(paymentModes)}
              </>
             : null
            }
            
            <View style={styles.subtitleWrapper}>
              <PtText style={styles.subtitle}>{labels.fileUploadPurchaseOrder}<PtText style={styles.asterix}>*</PtText></PtText>
            </View>
            <TextInput
              style={(filePickerStatus.filePicked && orderScenario && paymentTerm) && !!!purchaseOrder  ? [styles.textarea, styles.textareaWithError] : styles.textarea }
              onChangeText={(text) => setPurchaseOrder(text)}
              value={purchaseOrder}
              multiline={true}
              placeholder={labels.fileUploadPurchaseOrderPlaceholder}
              maxLength={6}
              editable={!(uploadStatus.uploadError || uploadStatus.uploadSuccess)}
            />

            <View style={styles.subtitleWrapper}>
              <PtText style={styles.subtitle}>{labels.fileUploadNotes}</PtText>
            </View>
            <TextInput
              style={!(uploadStatus.uploadError || uploadStatus.uploadSuccess) ? styles.textarea : [styles.textarea, styles.textareaDisabled]}
              onChangeText={(text) => setNotes(text)}
              value={notes}
              multiline={true}
              placeholder={labels.fileUploadNotesPlaceholder}
              maxLength={200}
              editable={!(uploadStatus.uploadError || uploadStatus.uploadSuccess)}
            />
          </>}
          {(props.uploadType === 'orders/xls' ? filePickerStatus.filePicked && orderScenario && paymentTerm && !!purchaseOrder : filePickerStatus.filePicked) ?
            !uploadStatus.uploadError && !uploadStatus.uploadSuccess ?
              <>
                <View style={styles.uploadControlsContainer}>
                  <View style={styles.uploadButtonContainer}>
                    <TouchableOpacity onPress={() => isAuthenticated ? validateForm() : null}>
                      <View style={[styles.uploadButton, disabledButtonStyle]}>
                        <PtText style={[styles.uploadButtonText, disabledTextStyle]}>{labels.fileUploadOrderButtonLabel} {filePickerStatus.fileName} [{filePickerStatus.fileSize}]</PtText>
                      </View>
                    </TouchableOpacity>
                  </View>
                  <View style={styles.cancelButtonContainer}>
                    <TouchableOpacity onPress={() => cancelUpload()}>
                      <View style={styles.cancelUploadButton}>
                        <PtText style={styles.cancelUploadButtonText}>{labels.cancelButtonLabel}</PtText>
                      </View>
                    </TouchableOpacity>
                  </View>
                </View>
                {!isAuthenticated ? <PtText style={{ fontSize: 10, textAlign: 'center' }}>{labels.noAuthText}</PtText> : null}
              </>
              :
              renderUploadDone(settings, labels, uploadStatus, uploadAnother, getErrorMessages, clearUploadError)
            : null
          }
        </View>
        :
        <>
          {props.showUnauthenticatedMessage ? 
            <View>
              <PtText style={{fontSize:styleVars.rem(.625), textAlign:'center'}}>
                {labels.noAuthText}
              </PtText>
            </View>
            :
            null
          }
        </>
      ) : null}
    </>
  )
}
